import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { epochLabel } from '@/features/projects/format'
import { useAppDispatch, useAppSelector } from '@/store'
import { selectCanWriteProject } from '@/features/projects/slices/projectDetailSlice'
import {
  selectDecidingGate,
  selectGates,
  submitGate
} from '../slices/phaseGatesSlice'
import type { PhaseGateView } from '../types'
import { ConfigSnapshotPanel } from './ConfigSnapshotPanel'
import { GateDecisionDialog } from './GateDecisionDialog'

/** Gate status → pill tone. */
const gateTone = (gate: PhaseGateView): StatusTone => {
  if (!gate.open) return 'decided'
  if (gate.decision === 'RETURN') return 'blocked'
  return 'proposed'
}

/** One gate card: status, ageing clock, criteria, decision, and the act buttons. */
function GateCard({
  orgId,
  projectId,
  gate,
  canWrite
}: {
  orgId: string
  projectId: string
  gate: PhaseGateView
  canWrite: boolean
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const [dialogOpen, setDialogOpen] = useState(false)
  const deciding = useAppSelector(selectDecidingGate)

  const statusLabel =
    !gate.open
      ? t('gates.approved')
      : gate.decision === 'RETURN'
        ? t('gates.returned')
        : gate.submittedBy !== undefined
          ? t('gates.submitted')
          : t('gates.open')

  const criteria = gate.criteria ?? []
  const met = criteria.filter((criterion) => criterion.state === 'MET').length

  return (
    <section
      aria-label={`${gate.step} — ${gate.title}`}
      className="rounded-lg border border-border bg-card px-4 py-3.5"
      data-testid={`gate-card-${gate.ref}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[0.7rem] font-semibold tracking-[0.06em] text-muted-foreground">
              {gate.step}
            </span>
            <StatusPill tone={gateTone(gate)}>{statusLabel}</StatusPill>
          </div>
          <h3 className="mt-1 font-serif text-base font-semibold text-foreground">{gate.title}</h3>
          {gate.gateTxt !== undefined && (
            <p className="mt-0.5 max-w-xl text-sm text-ink-soft">{gate.gateTxt}</p>
          )}
        </div>
        {gate.open && (
          <span className="v-figure shrink-0 rounded-full bg-muted px-2.5 py-1 text-xs tabular-nums text-muted-foreground">
            {t('gates.ageDays', { count: gate.ageDays ?? 0 })}
          </span>
        )}
      </div>

      {gate.prerequisiteRef !== undefined && gate.prerequisiteSatisfied === false && (
        <p className="mt-2 text-xs font-medium text-review">
          {t('gates.prerequisiteOpen', { ref: gate.prerequisiteRef })}
        </p>
      )}

      {criteria.length > 0 && (
        <ul className="mt-2.5">
          {criteria.map((criterion) => (
            <li key={criterion.key} className="v-row flex flex-wrap items-baseline gap-x-2 py-1 text-sm">
              <StatusPill tone={criterion.state === 'MET' ? 'done' : criterion.state === 'NOT_MET' ? 'blocked' : 'neutral'}>
                {criterion.state ?? '—'}
              </StatusPill>
              <span className="text-foreground">{criterion.text}</span>
              {criterion.note !== undefined && (
                <span className="text-xs text-muted-foreground">{criterion.note}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1.5 text-xs text-muted-foreground">{t('gates.criteriaMet', { met, total: criteria.length })}</p>

      {!gate.open && gate.decidedByName !== undefined && (
        <p className="mt-2 text-xs text-muted-foreground">
          {t('gates.decidedBy', { name: gate.decidedByName })}
          {gate.decidedAt !== undefined && ` · ${t('gates.decidedAt', { date: epochLabel(gate.decidedAt) })}`}
          {gate.overridden && (
            <span className="ml-1 rounded-full bg-review-bg px-2 py-0.5 text-[0.68rem] font-medium text-review-ink">
              {gate.overrideReason ?? 'override'}
            </span>
          )}
        </p>
      )}

      {gate.open && canWrite && (
        <div className="mt-3 flex flex-wrap gap-2">
          {gate.ref === 'flow.03' && gate.submittedBy === undefined && (
            <button
              type="button"
              disabled={deciding === gate.ref}
              onClick={() => void dispatch(submitGate({ orgId, projectId, gateRef: gate.ref }))}
              className="rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              {t('gates.submitFlow03')}
            </button>
          )}
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            data-testid={`gate-decide-${gate.ref}`}
          >
            {t('gates.approve')}
          </button>
        </div>
      )}
      {!gate.open && <p className="mt-2 text-xs text-muted-foreground">{t('gates.decided')}</p>}

      {dialogOpen && (
        <GateDecisionDialog
          orgId={orgId}
          projectId={projectId}
          gate={gate}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </section>
  )
}

/**
 * The four onboarding gates (AC-2). Approve/Return run through the decision
 * dialog; the flow.04 card grows the snapshot panel once that gate is
 * approved (AC-3). Actions are hidden for read-only callers — the server
 * projection decides (INV-9), never a client-side role re-derivation.
 */
export function GatesPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const gates = useAppSelector(selectGates)
  const canWrite = useAppSelector(selectCanWriteProject)
  const flow04 = gates.find((gate) => gate.ref === 'flow.04')

  return (
    <section aria-label={t('gates.panelTitle')}>
      <SectionLabel>{t('gates.panelTitle')}</SectionLabel>
      {gates.length === 0 ? (
        <p className="mt-1.5 rounded-lg border border-dashed border-border bg-card px-4 py-5 text-sm text-muted-foreground">
          {t('gates.empty')}
        </p>
      ) : (
        <div className="mt-2 grid gap-3 xl:grid-cols-2">
          {gates.map((gate) => (
            <GateCard key={gate.ref} orgId={orgId} projectId={projectId} gate={gate} canWrite={canWrite} />
          ))}
        </div>
      )}
      {flow04 !== undefined && !flow04.open && (
        <div className="mt-3">
          <ConfigSnapshotPanel orgId={orgId} projectId={projectId} />
        </div>
      )}
    </section>
  )
}
